import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import ExpertSupportCard from "./components/ExpertSupportCard";
import SupportTicketForm from "./components/SupportTicketForm";
import ContactInformation from "./components/ContactInformation";
import Icon from "../../components/AppIcon";

const ContactSupport = () => {
  const currentYear = new Date()?.getFullYear();

  return (
    <>
      <Helmet>
        <title>
          Contact Kharbanda Motors | India’s trusted car spare parts platform,
          offering reliable and genuine automotive parts.
        </title>

        <meta
          name="description"
          content="Kharbanda Motors | India’s most trusted platform for genuine car spare parts, and automobile accessories. From a leading dealer in Prayagraj to a nationwide supplier, we deliver reliable quality and 40+ years of expertise."
        />

        <meta
          name="keywords"
          content="car spare parts India, buy car parts online, OEM car parts India, genuine auto parts Prayagraj, Hyundai spare parts, Mahindra spare parts, Maruti car spares, Tata auto components, Toyota genuine parts, automotive spare parts dealer Uttar Pradesh, OEM parts Asia, auto parts supplier India, car accessories and components, Kharbanda Motors Prayagraj"
        />

        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://kharbandamotors.com/contact" />

        <meta
          property="og:title"
          content="About Kharbanda Motors | India’s trusted car spare parts platform, offering reliable and genuine automotive parts."
        />
        <meta
          property="og:description"
          content="Kharbanda Motors | India’s most trusted platform for genuine car spare parts, and automobile accessories. From a leading dealer in Prayagraj to a nationwide supplier, we deliver reliable quality and 40+ years of expertise."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://kharbandamotors.com/contact"
        />
        <meta
          property="og:image"
          content="https://kharbandamotors.com/assets/kharbanda-og-image.jpg"
        />

        <meta name="geo.region" content="IN-UP" />
        <meta name="geo.placename" content="Prayagraj" />
        <meta name="geo.position" content="25.4358;81.8463" />
        <meta name="ICBM" content="25.4358,81.8463" />
        <meta httpEquiv="content-language" content="en-IN" />

        <script type="application/ld+json">
          {`
    {
      "@context": "https://schema.org",
      "@type": "AutoPartsStore",
      "name": "Kharbanda Motors",
      "image": "https://kharbandamotors.com/Untitled-removebg-preview (11).webp",
      "url": "https://kharbandamotors.com/",
      "telephone": "+91 8573036150",
      "email": "contact@kharbandamotors.com",
      "priceRange": "₹₹",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "28, Nawab Yusuf Rd, Civil Lines",
        "addressLocality": "Prayagraj",
        "addressRegion": "Uttar Pradesh",
        "postalCode": "211001",
        "addressCountry": "IN"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 25.4358,
        "longitude": 81.8463
      },
      "openingHoursSpecification": [{
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": [
          "Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"
        ],
        "opens": "10:00",
        "closes": "20:00"
      }],
      "sameAs": [
        "https://www.facebook.com/kharbandamotors",
        "https://www.instagram.com/kharbandamotors",
      ],
      "areaServed": {
        "@type": "Place",
        "name": ["India", "South Asia", "Asia", "Worldwide"]
      },
      "description": "Kharbanda Motors is a trusted OEM car parts supplier in Prayagraj, Uttar Pradesh, India. Offering Hyundai, Mahindra, Maruti, Tata, and Toyota spare parts with worldwide delivery and quality assurance."
    }
    `}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background pt-8">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-accent/5 via-white to-secondary/5 pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="flex items-center justify-center space-x-3 mb-4">
                <div className="flex items-center justify-center w-16 h-16 bg-accent rounded-xl shadow-automotive-md">
                  <Icon
                    name="Headphones"
                    size={32}
                    color="white"
                    strokeWidth={2}
                  />
                </div>
                <div className="text-left">
                  <h1 className="text-4xl lg:text-5xl font-bold text-text-primary">
                    Contact & Support
                  </h1>
                  <p className="text-lg text-text-secondary">
                    Expert automotive assistance when you need it
                  </p>
                </div>
              </div>
              <p className="text-md text-text-secondary max-w-3xl mx-auto leading-relaxed">
                At Kharbanda Motors, our certified automotive experts are ready
                to help you with everything from spare part identification and
                compatibility verification to installation guidance and warranty
                queries.
                <br />
                We’re committed to ensuring your vehicle receives the right
                parts and professional service - every single time.
              </p>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="px-4 pb-12">
          <div className="max-w-7xl mx-auto sm:px-6 lg:px-8">
            <div className="lg:col-span-2 space-y-8">
              <ExpertSupportCard />
              <SupportTicketForm />
              <ContactInformation />
              {/* <FAQSection /> */}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-secondary text-white py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {/* Company Info */}
              <div className="md:col-span-2">
                <div className="flex items-center space-x-3">
                  <Link
                    to="/homepage"
                    className="flex items-center space-x-3 mb-6"
                  >
                    <div className="flex justify-center overflow-hidden">
                      <img
                        src="/Untitled-removebg-preview (11).webp"
                        alt="Kharbanda Motors Logo"
                        className="h-14 object-contain"
                      />
                    </div>
                  </Link>
                </div>
                <p className="text-white/80 mb-4 max-w-md">
                  Your trusted partner for genuine automotive parts with expert
                  support, guaranteed compatibility, and professional
                  installation services across India.
                </p>
                <div className="flex space-x-4">
                  {/* Phone */}
                  <a
                    href="tel:+918573036150"
                    className="flex items-center space-x-2 hover:text-accent transition"
                  >
                    <Icon name="Phone" size={16} className="text-accent" />
                    <span className="text-sm">+91 8573036150</span>
                  </a>

                  {/* Email */}
                  <a
                    href="mailto:contact@kharbandamotors.com"
                    className="flex items-center space-x-2 hover:text-accent transition"
                  >
                    <Icon name="Mail" size={16} className="text-accent" />
                    <span className="text-sm">contact@kharbandamotors.com</span>
                  </a>
                </div>
              </div>

              {/* Quick Links */}
              <div>
                <h4 className="font-semibold mb-4">Quick Links</h4>
                <ul className="space-y-2 text-sm text-white/80">
                  <li>
                    <a href="/" className="hover:text-accent transition-colors">
                      Home
                    </a>
                  </li>
                  <li>
                    <a
                      href="/about-us"
                      className="hover:text-accent transition-colors"
                    >
                      About
                    </a>
                  </li>
                  <li>
                    <a
                      href="/contact"
                      className="hover:text-accent transition-colors"
                    >
                      Contact
                    </a>
                  </li>
                </ul>
              </div>

              {/* Business Hours */}
              <div>
                <h4 className="font-semibold mb-4">Business Hours</h4>
                <ul className="space-y-2 text-sm text-white/80">
                  <li>Mon-Sat: 10:30 AM - 8:00 PM</li>
                  <li>Sunday: Closed</li>
                  <li className="text-accent">Emergency: 24/7</li>
                  <li>WhatsApp: Always Available</li>
                </ul>
              </div>
            </div>

            <div className="border-t border-white/20 mt-8 pt-8 text-center">
              <p className="text-white/60 text-sm">
                © {currentYear} Kharbanda Motors. All rights reserved. |
                <span className="text-accent">
                  {" "}
                  Trusted by 1.2K+ customers across India
                </span>
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ContactSupport;
